import styled from '@emotion/native';
import type { ColorName } from '@emotion/react';
import { LocationAcuityCode } from '@quromedical/fhir-common';
import type { UserPreferenceWard } from '@quromedical/models';
import { Col, Row } from 'components/base';
import type { SelectOption } from 'components/types';
import { COLOR_DARK } from 'design-system/theme';
import { View } from 'react-native';

interface DropdownContainerProps {
  isSmall: boolean;
}

export const WardWrapper = styled(Row)(() => ({
  marginBottom: 16,
  marginTop: 0,
  zIndex: -1,
}));

export const WardContainer = styled(Row)(() => ({
  gap: 16,
}));

export const SummaryItem = styled(Col)(() => ({
  alignItems: 'center',
}));

export const ItemRow = styled(Row)(() => ({
  alignItems: 'center',
}));

export const ItemRowWithMargin = styled(Row)<DropdownContainerProps>(
  ({ isSmall = false }) => ({
    alignItems: 'center',
    gap: isSmall ? 4 : 8,
    marginTop: 8,
  }),
);

export const AlignEndContainer = styled(Col)(() => ({
  justifyContent: 'flex-end',
  flex: 1,
}));

export const PageContainer = styled(Col)(() => ({
  flex: 1,
  alignItems: 'center',
  justifyContent: 'space-between',
}));

export const LeftColumn = styled(Col)(() => ({
  flex: 1,
}));

export const RightColumn = styled(Col)(() => ({
  flex: 1,
  width: '100%',
}));

export const ContentRow = styled(Row)(() => ({
  gap: 12,
  justifyContent: 'space-between',
  marginTop: 0,
}));

export const ModalBackground = styled(Col)(() => ({
  flex: 1,
  backgroundColor: 'rgba(0,0,0,0.4)',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
}));

export const TableWrapper = styled(Col)(() => ({
  zIndex: -1,
  overflow: 'visible',
}));

export const DropdownContainer = styled(View)<DropdownContainerProps>(
  ({ isSmall = false }) => ({
    position: 'relative',
    minWidth: isSmall ? 150 : 200,
  }),
);

export const DropdownButtonWrapper = styled(View)(() => ({
  backgroundColor: COLOR_DARK.divider,
  width: '100%',
  height: '100%',
  borderRadius: 4,
}));

export const DropdownListContainer = styled(View)(() => ({
  position: 'absolute',
  top: '100%',
  left: 0,
  backgroundColor: COLOR_DARK.divider,
  minWidth: 200,
  paddingVertical: 8,
  borderRadius: 6,
  justifyContent: 'center',
  alignItems: 'center',
}));

const acuityColorMap: Record<LocationAcuityCode, ColorName> = {
  [LocationAcuityCode.Intensive]: 'status-critical',
  [LocationAcuityCode.Medium]: 'accent-orange',
  [LocationAcuityCode.Low]: 'accent-green',
  [LocationAcuityCode.Unallocated]: 'white',
};

export function getAcuityColor(
  acuityCode: LocationAcuityCode | undefined,
): ColorName {
  return acuityCode
    ? acuityColorMap[acuityCode]
    : acuityColorMap[LocationAcuityCode.Unallocated];
}
type AcuitySelectOption = SelectOption & { acuity?: LocationAcuityCode };
export const matchWardsToLocationWard = (
  wards: UserPreferenceWard[],
  wardOptions: AcuitySelectOption[],
): UserPreferenceWard[] => {
  const matchedWardsToLocationWard = wards.map((ward) => {
    return wardOptions.find(
      (option) => option.value === ward.id && option.display === ward.name,
    );
  });

  return matchedWardsToLocationWard
    .filter(
      (
        w,
      ): w is { value: string; display: string; acuity?: LocationAcuityCode } =>
        !!w && typeof w.value === 'string',
    )
    .map((w) => ({
      id: w.value,
      name: w.display,
      acuity: w.acuity,
    }));
};
